<template>
  <div class="hydrogen-projects-map">
    <MapContainer :fluid="false">
      <MapGetter
        v-slot="{ map, popup }"
        type="mapbox"
        container="hydrogen-2023-projects"
        theme="mono"
        :maxBounds="locked ? bounds : null"
        :showBorder="false"
        :center="[0, 30]"
        :minZoom="1.5"
        :maxZoom="6"
        :zoom="1.5"
      >
        <Map
          id="hydrogen-2023-projects"
          :map="map"
          :popup="popup"
          :data="mapdata"
          :techColors="colors"
          :showMiscLayer="showMiscProjects"
          :selectedCountries="selectedCountries"
          :selectedRange="selectedRange"
          :deselectedStatus="deselectedStatus"
          :deselectedTech="deselectedTech"
        />
      </MapGetter>
      <MapControls>
        <MapControl>
          <div>
            <div class="slider-range-title">Commissioning year</div>
            <SliderRange
              v-model="selectedRange"
              :title="rangeLabel"
              :min="yearMinMax[0]"
              :max="yearMinMax[1]"
              :showValue="false"
            />
          </div>
          <Toggle
            small
            round
            v-model="showMiscProjects"
            beforeLabel="Unknown projects*"
          />
          <Multiselect
            small
            label="Select regions"
            :options="countryOptions"
            :value="selectedCountries"
            @input="selectCountry"
          />
          <ToggleGroup
            small
            title="Technology"
            :options="technologyOptions"
            @change="handleTechnology"
          />
          <ToggleGroup
            small
            title="Status"
            :options="statusOptions"
            @change="handleStatus"
          />
          <MapLegend :legend="legendOptions">
            <template v-slot:notes>
              <p>*Projects without announced commissioning year</p>
            </template>
          </MapLegend>
        </MapControl>
        <MapControl right bottom v-if="!locked">
          <ProjectChart :data="filteredData" :colors="colors" />
        </MapControl>
      </MapControls>
    </MapContainer>
  </div>
</template>

<script>
import {
  MapGetter,
  MapContainer,
  MapControls,
  MapControl,
  MapLegend,
} from "@international-energy-agency/vue-ui/maps";
import {
  Multiselect,
  SliderRange,
  Toggle,
} from "@international-energy-agency/vue-ui";
import ToggleGroup from "./components/ToggleGroup.vue";
import ProjectChart from "./components/ProjectChart.vue";
import Map from "./components/ProjectsMap.vue";
import data from "./assets/data.json";
import countryISO3 from "./assets/countryISO3.json";
export default {
  name: "App",
  components: {
    MapGetter,
    MapContainer,
    MapControls,
    MapControl,
    Multiselect,
    SliderRange,
    Toggle,
    ToggleGroup,
    ProjectChart,
    MapLegend,
    Map,
  },
  props: {
    locked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      data: [],
      mapdata: null,
      chartData: null,
      bounds: [
        [-18, 47],
        [18, 63],
      ],
      selectedRange: [1999, 2031],
      yearMinMax: [1999, 2031],
      selectedCountries: [],
      showMiscProjects: false,
      technologyOptions: [],
      statusOptions: [],
      deselectedStatus: [],
      deselectedTech: [],
      colors: {
        Electrolysis: { index: 3, color: "#00ada1" },
        "Fossil fuels with CCUS": { index: 1, color: "#3e7ad3" },
        Other: { index: 6, color: "#ff754b" },
      },
      legendOptions: [
        {
          type: "bubble",
          title: "Projet capacity (kt H₂/yr)",
          items: [
            { size: 35, label: "> 5 000" },
            { size: 21, label: "1 000-5 000" },
            { size: 14, label: "250-500" },
            { size: 7, label: "150-250" },
            { size: 3, label: "50-150" },
            { size: 1, label: "< 50" },
          ],
        },
      ],
    };
  },
  created() {
    this.init();
  },
  computed: {
    toggleLabel() {
      return this.showMiscProjects ? "Hide misc* projects" : "Projects";
    },
    rangeLabel() {
      const { selectedRange, yearMinMax } = this;
      let [min, max] = selectedRange;
      min =
        Number(min) === yearMinMax[0]
          ? "< 2000"
          : Number(min) === yearMinMax[1]
          ? "> 2030"
          : min;
      max =
        Number(max) === yearMinMax[1]
          ? "> 2030"
          : Number(max) === yearMinMax[0]
          ? "< 2000"
          : max;
      return min === max ? `${min}` : ` ${min} to ${max}`;
    },
    countryOptions() {
      const { selectedRange, data } = this;
      const [min, max] = selectedRange;
      const unique = [
        ...new Set(
          data
            .filter(
              (d) =>
                d.hasYear &&
                d.hasCoord &&
                d.indexedYear >= Number(min) &&
                d.indexedYear <= Number(max)
            )
            .map((d) => d.country)
        ),
      ].map((country) => {
        const exists = countryISO3.find((d) => d.ISO3 === country);
        if (exists) return { value: country, label: exists.region };
      });
      return unique.sort((a, b) => a.label.localeCompare(b.label));
    },
    filteredData() {
      const { data, datafilter, colors } = this;
      if (data.length === 0) {
        return [];
      } else {
        const capacityKey = "capacity_mw";
        const productionKey = "capacity_kt";
        const noCoordHasYear = data.filter((d) => !d.hasCoord && d.hasYear);
        const hasCoordHasYear = data.filter((d) => d.hasCoord && d.hasYear);

        let temp = [
          ...hasCoordHasYear.filter(datafilter),
          ...noCoordHasYear.filter(datafilter),
        ];

        const capValue = temp.reduce(
          (acc, curr) => acc + Number(curr[capacityKey]),
          0
        );

        const prodValue = Object.entries(
          temp.reduce((acc, curr) => {
            acc[curr["technology"]] = acc[curr["technology"]] || 0;
            acc[curr["technology"]] += curr[productionKey];
            return acc;
          }, {})
        );
        return {
          totalProjects: temp.length,
          data:
            capValue === 0 && prodValue.length === 0
              ? []
              : [
                  {
                    id: "capacity",
                    show: capValue === 0 ? false : true,
                    label: "Installed electrolysis capacity by 2030",
                    unit: "MW",
                    series: [
                      { name: "Capacity", data: [capValue], colorIndex: 5 },
                    ],
                  },
                  {
                    id: "leProduction",
                    show: prodValue.length === 0 ? false : true,
                    label: "Low-emissions hydrogen production capacity by 2030",
                    unit: "kt H₂/yr",
                    series: prodValue
                      .map((d) => ({
                        name: d[0],
                        data: [d[1]],
                        colorIndex: colors[d[0]].index,
                      }))
                      .sort((a, b) => a.data[0] - b.data[0]),
                  },
                ],
        };
      }
    },
  },
  methods: {
    datafilter(d, year) {
      const {
        statusOptions,
        technologyOptions,
        selectedCountries,
        selectedRange,
        showMiscProjects,
      } = this;

      let [min, max] = selectedRange;
      min = Number(min);
      max = Number(max);
      const statusFilter = statusOptions
        .filter((d) => d.value === true)
        .map((d) => d.label);
      const techFilter = technologyOptions
        .filter((d) => d.value === true)
        .map((d) => d.label);
      if (year === "noyear") {
        return (
          (showMiscProjects ? d : d.hasYear) &&
          (selectedCountries.length === 0
            ? d
            : selectedCountries.includes(d.country)) &&
          statusFilter.includes(d.status) &&
          techFilter.includes(d.technology)
        );
      } else {
        return (
          (selectedCountries.length === 0
            ? d
            : selectedCountries.includes(d.country)) &&
          statusFilter.includes(d.status) &&
          techFilter.includes(d.technology) &&
          (min === max
            ? d.indexedYear === min
            : d.indexedYear >= min && d.indexedYear <= max)
        );
      }
    },
    init() {
      this.data = data.map((d) => ({
        ...d,
        hasCoord: d.latitude || d.longitude ? true : false,
        hasYear: d.year ? true : false,
        indexedYear: d.year > 2030 ? 2031 : d.year,
      }));

      this.technologyOptions = [...new Set(data.map((d) => d.technology))]
        .map((d) => ({ value: true, label: d }))
        .sort((a, b) => a.label.localeCompare(b.label));

      this.statusOptions = [...new Set(data.map((d) => d.status))]
        .map((d) => ({ value: true, label: d }))
        .sort((a, b) => a.label.localeCompare(b.label));

      this.mapdata = this.data
        .filter((d) => d.hasCoord)
        .map((d) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [d.longitude, d.latitude],
          },
          properties: { ...d, hasYear: d.hasYear ? 1 : 0 },
        }))
        .sort((a, b) => b.properties.capacity_kt - a.properties.capacity_kt);
    },
    selectCountry(countries) {
      this.selectedCountries = countries;
    },
    handleTechnology() {
      let temp = [...this.technologyOptions];
      this.deselectedTech = temp.filter((d) => !d.value).map((d) => d.label);
    },
    handleStatus() {
      let temp = [...this.statusOptions];
      this.deselectedStatus = temp.filter((d) => !d.value).map((d) => d.label);
    },
  },
};
</script>

<style>
.hydrogen-projects-map .top-l .map-control__inner {
  width: 235px;
  border-radius: 8px;
  max-height: 740px;
  overflow-y: scroll;
}
.hydrogen-projects-map .btm-r .map-control__inner {
  width: 192px;
  border-radius: 8px;
}
.hydrogen-projects-map .input-slider-range {
  width: unset;
  margin: unset;
  padding: 0 10px;
}
.hydrogen-projects-map .input-slide-range__title {
  margin-bottom: 8px !important;
  font-weight: 400 !important;
}
.hydrogen-projects-map .slider-range-title {
  /* margin-top: 10px; */
  padding: 0 10px;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 0.875rem;
}
.hydrogen-projects-map .toggle__label {
  display: flex;
  padding: 0 10px;
  width: 100%;
  justify-content: space-between;
}
.hydrogen-projects-map .toggle__label-text {
  font-size: 0.8rem !important;
  line-height: 0.8rem !important;
}
.toggle-group > span {
  font-size: 0.875rem;
  font-weight: 600;
}
.toggle-group {
  margin-bottom: 10px;
}
.toggle-group .a-input {
  margin-top: 8px;
}
.hydrogen-projects-map .control-notes {
  border-top: 1px solid #000;
  box-sizing: border-box;
  font-size: 0.65rem;
  line-height: 0.65rem;
  margin-top: 10px;
  padding: 10px 0 0 0;
}
</style>
